/**
 * ITER302-refine-v2 · Rich multi-layer ribbon artwork.
 *
 * Every variant renders with 3 accent shades (highlight / base / shadow)
 * for a hand-crafted look — not flat single-fill polygons.  All curves
 * are actual bezier paths so the ribbons read as 3D on-screen.
 */
import React from 'react';
import { accentShades } from './voucherDesign';

export default function VoucherRibbon({
  variant = 'corner_bow',
  color = '#C9A227',
  badgeText = '',
}) {
  if (!variant || variant === 'none') return null;
  const s = accentShades(color);

  // ─── Realistic gift-wrap bow in the top-right corner ─────────────
  if (variant === 'corner_bow') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 100 100"
        className="absolute top-1 right-1 w-20 h-20 pointer-events-none drop-shadow-md"
      >
        {/* Two crossed ribbon tails coming out of the knot */}
        <path
          d="M50 42 C60 58, 66 70, 70 92 L58 92 C56 74, 52 62, 44 50 Z"
          fill={s.base}
        />
        <path
          d="M50 42 C66 46, 78 48, 96 44 L96 56 C82 60, 68 60, 52 54 Z"
          fill={s.base}
        />
        {/* Highlight edges */}
        <path d="M46 44 C60 50, 72 52, 96 46" stroke={s.light} strokeWidth="1.2" fill="none" />
        <path d="M52 44 C58 60, 62 76, 66 90"  stroke={s.light} strokeWidth="1.2" fill="none" />
        {/* Left loop of the bow — bezier curves for a rounded loop */}
        <path
          d="M50 34 C28 20, 12 26, 12 40 C12 52, 30 46, 50 40 Z"
          fill={s.base}
        />
        <path
          d="M50 34 C28 20, 12 26, 12 40"
          stroke={s.light} strokeWidth="1.2" fill="none"
        />
        {/* Right loop */}
        <path
          d="M50 34 C72 20, 88 26, 88 40 C88 52, 70 46, 50 40 Z"
          fill={s.base}
        />
        <path
          d="M50 34 C72 20, 88 26, 88 40"
          stroke={s.light} strokeWidth="1.2" fill="none"
        />
        {/* Fold shadows inside each loop */}
        <path
          d="M50 34 C34 30, 20 34, 18 40 C24 38, 36 36, 50 38 Z"
          fill={s.shadow} opacity="0.55"
        />
        <path
          d="M50 34 C66 30, 80 34, 82 40 C76 38, 64 36, 50 38 Z"
          fill={s.shadow} opacity="0.55"
        />
        {/* Center knot — the visual anchor */}
        <ellipse cx="50" cy="38" rx="8" ry="6" fill={s.shadow} />
        <ellipse cx="50" cy="37" rx="6" ry="4" fill={s.base} />
        <ellipse cx="50" cy="36" rx="3.5" ry="2" fill={s.light} opacity="0.9" />
      </svg>
    );
  }

  // ─── Diagonal sash with fold + optional badge text ───────────────
  if (variant === 'diagonal') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 260 60"
        preserveAspectRatio="none"
        className="absolute pointer-events-none drop-shadow-md"
        style={{
          top: 26, right: -30, width: 200, height: 34,
          transform: 'rotate(28deg)',
        }}
      >
        {/* Base ribbon */}
        <rect x="0" y="4" width="260" height="52" fill={s.base} />
        {/* Highlight edge along the top */}
        <rect x="0" y="4"  width="260" height="4"  fill={s.light} opacity="0.8" />
        {/* Shadow edge along the bottom */}
        <rect x="0" y="52" width="260" height="4"  fill={s.shadow} />
        {/* Center fold — a subtle darker band that suggests a twist */}
        <path d="M115 4 L145 4 L138 56 L108 56 Z" fill={s.shadow} opacity="0.45" />
        {badgeText && (
          <text
            x="130" y="36" textAnchor="middle"
            fontSize="18" fontWeight="700"
            fontFamily="Montserrat, sans-serif"
            fill="#FFFFFF" opacity="0.95"
            letterSpacing="2"
          >
            {String(badgeText).toUpperCase().slice(0, 14)}
          </text>
        )}
      </svg>
    );
  }

  // ─── Legacy simple banner (kept for backward compat) ────────────
  if (variant === 'banner') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 240 40"
        preserveAspectRatio="none"
        className="absolute top-0 left-0 w-full h-7 pointer-events-none"
      >
        <path d="M0 0 L240 0 L240 40 L120 30 L0 40 Z" fill={s.base} />
        <path d="M0 0 L240 0 L240 4 L0 4 Z" fill={s.light} opacity="0.7" />
        <path d="M120 30 L240 40 L240 36 L120 26 L0 36 L0 40 Z" fill={s.shadow} opacity="0.6" />
      </svg>
    );
  }

  if (variant === 'side_accent') {
    return (
      <div
        aria-hidden="true"
        className="absolute top-0 left-0 h-full w-2 pointer-events-none"
        style={{
          background: `linear-gradient(to right, ${s.shadow} 0%, ${s.base} 45%, ${s.light} 100%)`,
        }}
      />
    );
  }

  // ─── Full-width top banner with scroll-fold ends + centered text ─
  if (variant === 'top_banner') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 240 44"
        preserveAspectRatio="none"
        className="absolute top-0 left-0 w-full h-10 pointer-events-none drop-shadow-md"
      >
        {/* Main banner body */}
        <path d="M14 0 L226 0 L226 30 L120 38 L14 30 Z" fill={s.base} />
        {/* Left scroll-fold */}
        <path d="M14 0 L0 8 L0 22 L14 30 Z" fill={s.shadow} />
        <path d="M14 0 L4 6 L4 24 L14 30 Z" fill={s.base} />
        {/* Right scroll-fold */}
        <path d="M226 0 L240 8 L240 22 L226 30 Z" fill={s.shadow} />
        <path d="M226 0 L236 6 L236 24 L226 30 Z" fill={s.base} />
        {/* Top highlight strip */}
        <rect x="14" y="0" width="212" height="4" fill={s.light} opacity="0.7" />
        {/* Bottom fold shadow (the V-notch at the bottom) */}
        <path d="M120 38 L14 30 L14 34 L120 42 L226 34 L226 30 Z" fill={s.shadow} opacity="0.55" />
        {badgeText && (
          <text
            x="120" y="20" textAnchor="middle"
            fontSize="14" fontWeight="700"
            fontFamily="Montserrat, sans-serif"
            fill="#FFFFFF" opacity="0.95"
            letterSpacing="3"
          >
            {String(badgeText).toUpperCase().slice(0, 24)}
          </text>
        )}
      </svg>
    );
  }

  // ─── Bottom sash across the bottom-left corner ───────────────────
  if (variant === 'bottom_sash') {
    return (
      <svg
        aria-hidden="true"
        viewBox="0 0 260 60"
        preserveAspectRatio="none"
        className="absolute pointer-events-none drop-shadow-md"
        style={{
          bottom: 22, left: -30, width: 200, height: 34,
          transform: 'rotate(-24deg)',
        }}
      >
        <rect x="0" y="4"  width="260" height="52" fill={s.base} />
        <rect x="0" y="4"  width="260" height="4"  fill={s.light} opacity="0.8" />
        <rect x="0" y="52" width="260" height="4"  fill={s.shadow} />
        <path d="M115 4 L145 4 L138 56 L108 56 Z" fill={s.shadow} opacity="0.45" />
        {badgeText && (
          <text
            x="130" y="36" textAnchor="middle"
            fontSize="18" fontWeight="700"
            fontFamily="Montserrat, sans-serif"
            fill="#FFFFFF" opacity="0.95"
            letterSpacing="2"
          >
            {String(badgeText).toUpperCase().slice(0, 14)}
          </text>
        )}
      </svg>
    );
  }

  // ─── Double corner — two bows for a symmetrical ornate look ──────
  if (variant === 'double_corner') {
    const bow = (translate, scale) => (
      <g transform={`translate(${translate}) ${scale || ''}`}>
        <path d="M50 34 C28 20, 12 26, 12 40 C12 52, 30 46, 50 40 Z" fill={s.base} />
        <path d="M50 34 C72 20, 88 26, 88 40 C88 52, 70 46, 50 40 Z" fill={s.base} />
        <path d="M50 34 C28 20, 12 26, 12 40" stroke={s.light} strokeWidth="1" fill="none" />
        <path d="M50 34 C72 20, 88 26, 88 40" stroke={s.light} strokeWidth="1" fill="none" />
        <ellipse cx="50" cy="38" rx="7" ry="5" fill={s.shadow} />
        <ellipse cx="50" cy="37" rx="5" ry="3" fill={s.base} />
        <ellipse cx="50" cy="36" rx="3" ry="1.5" fill={s.light} opacity="0.9" />
      </g>
    );
    return (
      <>
        <svg aria-hidden="true" viewBox="0 0 100 60"
             className="absolute top-1 right-1 w-16 h-10 pointer-events-none drop-shadow-md">
          {bow('0,0')}
        </svg>
        <svg aria-hidden="true" viewBox="0 0 100 60"
             className="absolute bottom-1 left-1 w-16 h-10 pointer-events-none drop-shadow-md"
             style={{ transform: 'scale(-1,-1)' }}>
          {bow('0,0')}
        </svg>
      </>
    );
  }

  // ─── Wrap ribbon at ~38 % height with left/right visible edges +
  //     tied center bow. Content flows above and below. ─────────────
  if (variant === 'wrap_ribbon') {
    return (
      <div
        aria-hidden="true"
        className="absolute left-0 right-0 pointer-events-none"
        style={{ top: '38%', height: 34 }}
      >
        {/* Ribbon body */}
        <div
          className="absolute inset-x-0 h-full"
          style={{
            background: `linear-gradient(180deg, ${s.light} 0%, ${s.base} 30%, ${s.base} 70%, ${s.shadow} 100%)`,
            boxShadow: `0 2px 6px rgba(0,0,0,0.25)`,
          }}
        />
        {/* Wrapped edges — a small tab visible on each card edge as if
            the ribbon continues around the back of the card. */}
        <div className="absolute -left-1 top-0 h-full w-3"
             style={{ background: s.shadow, clipPath: 'polygon(0 0, 100% 12%, 100% 88%, 0 100%)' }} />
        <div className="absolute -right-1 top-0 h-full w-3"
             style={{ background: s.shadow, clipPath: 'polygon(0 12%, 100% 0, 100% 100%, 0 88%)' }} />
        {/* Centre bow */}
        <svg
          viewBox="0 0 60 46"
          className="absolute left-1/2 -translate-x-1/2 drop-shadow-md"
          style={{ top: -8, width: 60, height: 46 }}
        >
          {/* Left loop */}
          <path d="M30 22 C10 6, -4 16, 4 26 C10 32, 22 28, 30 24 Z" fill={s.base} />
          <path d="M30 22 C10 6, -4 16, 4 26" stroke={s.light} strokeWidth="1" fill="none" />
          <path d="M30 22 C18 20, 8 24, 4 26" fill={s.shadow} opacity="0.5" />
          {/* Right loop */}
          <path d="M30 22 C50 6, 64 16, 56 26 C50 32, 38 28, 30 24 Z" fill={s.base} />
          <path d="M30 22 C50 6, 64 16, 56 26" stroke={s.light} strokeWidth="1" fill="none" />
          <path d="M30 22 C42 20, 52 24, 56 26" fill={s.shadow} opacity="0.5" />
          {/* Tails */}
          <path d="M26 26 C24 34, 22 40, 18 44 L14 40 C18 36, 22 32, 24 26 Z" fill={s.base} />
          <path d="M34 26 C36 34, 38 40, 42 44 L46 40 C42 36, 38 32, 36 26 Z" fill={s.base} />
          {/* Center knot */}
          <ellipse cx="30" cy="24" rx="6" ry="4" fill={s.shadow} />
          <ellipse cx="30" cy="23" rx="4" ry="2.5" fill={s.base} />
          <ellipse cx="30" cy="22" rx="2.5" ry="1.2" fill={s.light} opacity="0.9" />
        </svg>
      </div>
    );
  }

  return null;
}
